import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { BookingSlot, SelectedScreenMeta } from '@/features/campaigns/types/campaign';

export type ScheduleDisplayGroup = {
  id: string;
  name: string;
  location?: string;
  orientation?: string;
  slots: BookingSlot[];
};

export type ScheduleSummaryApi = {
  displays: ScheduleDisplayGroup[];
  onRemoveSlot: (billboardId: string, date: string, hour: number) => void;
  onClearDisplay?: (billboardId: string) => void;
  onClearAll?: () => void;
};

type ScheduleSummaryContextValue = {
  open: boolean;
  openSummary: () => void;
  closeSummary: () => void;
  api: ScheduleSummaryApi | null;
  registerApi: (api: ScheduleSummaryApi | null) => void;
  totalSlots: number;
};

const ScheduleSummaryContext = createContext<ScheduleSummaryContextValue | null>(
  null,
);

export function ScheduleSummaryProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [api, setApi] = useState<ScheduleSummaryApi | null>(null);

  const registerApi = useCallback((next: ScheduleSummaryApi | null) => {
    setApi(next);
  }, []);

  const totalSlots = useMemo(
    () => api?.displays.reduce((sum, d) => sum + d.slots.length, 0) ?? 0,
    [api],
  );

  const value = useMemo(
    () => ({
      open,
      openSummary: () => setOpen(true),
      closeSummary: () => setOpen(false),
      api,
      registerApi,
      totalSlots,
    }),
    [open, api, registerApi, totalSlots],
  );

  return (
    <ScheduleSummaryContext.Provider value={value}>
      {children}
    </ScheduleSummaryContext.Provider>
  );
}

export function useScheduleSummary() {
  const ctx = useContext(ScheduleSummaryContext);
  if (!ctx) {
    return {
      open: false,
      openSummary: () => undefined,
      closeSummary: () => undefined,
      api: null as ScheduleSummaryApi | null,
      registerApi: (_api: ScheduleSummaryApi | null) => undefined,
      totalSlots: 0,
    };
  }
  return ctx;
}

/** Build display groups from flat booking slots + screen meta. */
export function buildScheduleDisplayGroups(
  bookingSlots: BookingSlot[],
  selectedMeta: Record<string, SelectedScreenMeta> | SelectedScreenMeta[] | undefined,
  billboardIds: string[] = [],
): ScheduleDisplayGroup[] {
  const metaList: SelectedScreenMeta[] = Array.isArray(selectedMeta)
    ? selectedMeta
    : selectedMeta
      ? Object.values(selectedMeta)
      : [];

  const metaById = new Map(metaList.map((m) => [m.id, m]));
  const ids =
    billboardIds.length > 0
      ? billboardIds
      : [
          ...new Set([
            ...metaList.map((m) => m.id),
            ...bookingSlots.map((s) => s.billboardId).filter(Boolean),
          ]),
        ].filter(Boolean) as string[];

  const byScreen = new Map<string, BookingSlot[]>();
  bookingSlots.forEach((slot) => {
    const id = slot.billboardId || ids[0] || 'default';
    const list = byScreen.get(id) || [];
    list.push(slot);
    byScreen.set(id, list);
  });

  const orderedIds =
    ids.length > 0 ? ids : [...byScreen.keys()];

  return orderedIds.map((id, index) => {
    const meta = metaById.get(id);
    const slots = byScreen.get(id) || [];
    return {
      id,
      name: meta?.screenName || `Display ${index + 1}`,
      location:
        [meta?.addressLine, meta?.city].filter(Boolean).join(', ') ||
        meta?.city,
      orientation: meta?.screenOrientation,
      slots,
    };
  });
}
